/**
 * Literal passed to `new Error(...)` in apiService for non-OK responses without a clearer message chain.
 * {@link getAuthApiDisplayMessage} treats this as empty so callers can show translated fallbacks.
 */
export const GENERIC_API_FAILED_MESSAGE = 'API request failed';

/** Thrown when retry after token refresh returns non-OK with no usable JSON body message. */
export const GENERIC_API_FAILED_AFTER_REFRESH_MESSAGE = 'API request failed after token refresh';

/** `/accounts/refresh/` returned non-OK with 401 */
export const API_SESSION_EXPIRED_MESSAGE = 'Session expired';

/** `/accounts/refresh/` returned non-OK with status other than 401 */
export const API_TOKEN_REFRESH_FAILED_MESSAGE = 'Token refresh failed';

/** Refresh JSON missing `access_token` */
export const API_INVALID_REFRESH_RESPONSE_MESSAGE = 'Invalid refresh response';

/** 401 on `/auth/login`, refresh, or logout — cannot recover in-request */
export const API_AUTH_REQUIRED_MESSAGE = 'Authentication failed. Please login again.';

/** Retry after refresh failed; underlying error was 401 */
export const API_SESSION_EXPIRED_LOGIN_AGAIN_MESSAGE = 'Session expired. Please login again.';

/** Retry after refresh failed; underlying error was not 401 */
export const API_REQUEST_FAILED_RETRY_MESSAGE = 'Request failed. Please try again.';

/** XHR upload helpers (postWithProgress, batch, single-file) */
export const API_PARSE_RESPONSE_FAILED_MESSAGE = 'Failed to parse response';

export const API_UPLOAD_FAILED_MESSAGE = 'Upload failed';

export const API_UPLOAD_NETWORK_ERROR_MESSAGE = 'Network error during upload';

export const API_UPLOAD_ABORTED_MESSAGE = 'Upload aborted';

export const API_BATCH_UPLOAD_FAILED_MESSAGE = 'Batch upload failed';

export const API_BATCH_UPLOAD_NETWORK_ERROR_MESSAGE = 'Network error during batch upload';

export const API_BATCH_UPLOAD_ABORTED_MESSAGE = 'Batch upload aborted';

export const API_UPLOAD_TIMEOUT_MANY_FILES_MESSAGE =
  'Upload timeout - server took too long to respond. Please try again with fewer files.';

export const API_UPLOAD_TIMEOUT_MESSAGE =
  'Upload timeout - server took too long to respond. Please try again.';

/** `albumDownload.js` / fetch helpers — keep literals stable for {@link translateKnownApiClientMessage} */
export const CLIENT_DOWNLOAD_FAILED_MESSAGE = 'Download failed';

export const CLIENT_DOWNLOAD_ACCESS_DENIED_MESSAGE = 'Access denied';

export const CLIENT_ALBUM_CANNOT_LOAD_DETAILS_MESSAGE = 'Cannot load album details';

export const CLIENT_NO_TRACKS_TO_DOWNLOAD_MESSAGE = 'No tracks to download';

export const CLIENT_NO_DOWNLOAD_LINK_MESSAGE = 'No download link';

export const CLIENT_NO_FILE_URL_MESSAGE = 'No file URL';

export const CLIENT_FETCH_UNAVAILABLE_MESSAGE = 'fetch unavailable';

/** Shop list components — vote/download guards */
export const CLIENT_NO_ALBUM_MESSAGE = 'No album';

export const CLIENT_NO_TRACK_MESSAGE = 'No track';

/** Admin bulk download when API omits `download_url` */
export const CLIENT_NO_DOWNLOAD_URL_RECEIVED_MESSAGE = 'No download URL received';

/** Username availability check — unexpected payload shape */
export const CLIENT_INVALID_RESPONSE_FORMAT_MESSAGE = 'Invalid response format';

/** Registration API often includes these when password and confirmation differ */
export const REGISTRATION_PASSWORD_MISMATCH_MARKERS = [
  'password_confirm',
  'Passwords do not match',
  'The two password fields',
  'Wprowadzone hasła nie są zgodne',
  'hasła muszą być identyczne',
  'Pole hasła (ponownie)',
];

/** Used by AuthPage to route API error text to the confirm-password field */
export function registrationErrorLooksLikePasswordMismatch(errorText) {
  if (!errorText || typeof errorText !== 'string') return false;
  return REGISTRATION_PASSWORD_MISMATCH_MARKERS.some((m) => errorText.includes(m));
}

/** Infinite scroll hook — malformed list payload */
export const CLIENT_INVALID_API_MISSING_RESULTS_MESSAGE =
  'Invalid API response - missing results array';

export const CLIENT_INVALID_API_RESULTS_NOT_ARRAY_MESSAGE =
  'Invalid API response - results is not an array';

/** When `Error.message` matches known client literals, map to i18n if `t` is passed */
const KNOWN_CLIENT_MESSAGE_I18N_KEYS = {
  [API_SESSION_EXPIRED_MESSAGE]: 'auth:errors.session_expired',
  [API_TOKEN_REFRESH_FAILED_MESSAGE]: 'auth:errors.token_refresh_failed',
  [API_INVALID_REFRESH_RESPONSE_MESSAGE]: 'auth:errors.invalid_refresh_response',
  [API_AUTH_REQUIRED_MESSAGE]: 'auth:errors.auth_required_relogin',
  [API_SESSION_EXPIRED_LOGIN_AGAIN_MESSAGE]: 'auth:errors.session_expired_login_again',
  [API_REQUEST_FAILED_RETRY_MESSAGE]: 'auth:errors.request_failed_retry',
  [API_PARSE_RESPONSE_FAILED_MESSAGE]: 'common:messages.api_parse_response_failed',
  [API_UPLOAD_FAILED_MESSAGE]: 'common:messages.api_upload_failed',
  [API_UPLOAD_NETWORK_ERROR_MESSAGE]: 'common:messages.api_upload_network',
  [API_UPLOAD_ABORTED_MESSAGE]: 'common:messages.api_upload_aborted',
  [API_BATCH_UPLOAD_FAILED_MESSAGE]: 'common:messages.api_batch_upload_failed',
  [API_BATCH_UPLOAD_NETWORK_ERROR_MESSAGE]: 'common:messages.api_batch_upload_network',
  [API_BATCH_UPLOAD_ABORTED_MESSAGE]: 'common:messages.api_batch_upload_aborted',
  [API_UPLOAD_TIMEOUT_MANY_FILES_MESSAGE]: 'common:messages.api_upload_timeout_many_files',
  [API_UPLOAD_TIMEOUT_MESSAGE]: 'common:messages.api_upload_timeout',
  [CLIENT_DOWNLOAD_FAILED_MESSAGE]: 'common:content.download_failed',
  [CLIENT_DOWNLOAD_ACCESS_DENIED_MESSAGE]: 'common:accessError.title',
  [CLIENT_ALBUM_CANNOT_LOAD_DETAILS_MESSAGE]: 'common:messages.album_download_cannot_load_details',
  [CLIENT_NO_TRACKS_TO_DOWNLOAD_MESSAGE]: 'common:dashboard.no_tracks_to_download',
  [CLIENT_NO_DOWNLOAD_LINK_MESSAGE]: 'common:messages.album_download_no_link',
  [CLIENT_NO_FILE_URL_MESSAGE]: 'common:messages.album_download_no_file_url',
  [CLIENT_FETCH_UNAVAILABLE_MESSAGE]: 'common:messages.fetch_unavailable',
  [CLIENT_NO_ALBUM_MESSAGE]: 'common:messages.shop_no_album',
  [CLIENT_NO_TRACK_MESSAGE]: 'common:messages.shop_no_track',
  [CLIENT_NO_DOWNLOAD_URL_RECEIVED_MESSAGE]: 'common:messages.admin_download_no_url',
  [CLIENT_INVALID_RESPONSE_FORMAT_MESSAGE]: 'common:messages.check_response_invalid',
  [CLIENT_INVALID_API_MISSING_RESULTS_MESSAGE]: 'common:messages.infinite_scroll_missing_results',
  [CLIENT_INVALID_API_RESULTS_NOT_ARRAY_MESSAGE]: 'common:messages.infinite_scroll_results_not_array',
};

function translateKnownApiClientMessage(message, t) {
  if (typeof t !== 'function' || typeof message !== 'string' || !message) return '';
  const key = KNOWN_CLIENT_MESSAGE_I18N_KEYS[message];
  return key ? String(t(key)) : '';
}

function collectMessagesFromDetails(details) {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return [];
  const out = [];
  Object.keys(details).forEach((key) => {
    const v = details[key];
    if (Array.isArray(v)) {
      v.forEach((item) => {
        if (typeof item === 'string') out.push(item);
        else if (item != null && typeof item === 'object' && typeof item.message === 'string') {
          out.push(item.message);
        }
      });
    } else if (typeof v === 'string') {
      out.push(v);
    }
  });
  return out;
}

/**
 * Stable machine-readable code from API errors: v2 `code`, then legacy `error`.
 * @param {object} [error] - Thrown API error (may have .data or .response.data)
 * @returns {string}
 */
export function getApiSemanticCode(error) {
  const d = error?.data ?? error?.response?.data;
  if (!d || typeof d !== 'object') return '';
  if (typeof d.error_code === 'string') return d.error_code;
  if (typeof d.code === 'string') return d.code;
  if (typeof d.error === 'string') return d.error;
  return '';
}

/**
 * Use with raw `fetch`: attach HTTP status + JSON body so {@link getDisplayErrorMessage} can read v2 / DRF payloads.
 * @param {Response} response
 * @param {Record<string, unknown>} [parsedBody] - Result of `response.json()` when available
 * @returns {Error & { status: number, data: Record<string, unknown> }}
 */
export function errorFromFetchResponse(response, parsedBody) {
  const err = new Error(`HTTP ${response.status}`);
  err.status = response.status;
  err.data =
    parsedBody != null && typeof parsedBody === 'object' && !Array.isArray(parsedBody)
      ? /** @type {Record<string, unknown>} */ (parsedBody)
      : {};
  return err;
}

function stringifyPerFileError(val) {
  if (typeof val === 'string') return val;
  if (val != null && typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val ?? '');
}

/**
 * Per-file batch upload errors: legacy top-level `errors` or v2 `details.errors`
 * (see `_v2_wrap_tracks_json_http_response` in api_v2).
 * @param {object} [error] - API error with `.data` or axios-style `.response.data`
 * @returns {Record<string, string>|null} Map filename → message, or null if none
 */
export function collectBatchUploadErrorsMap(error) {
  const d = error?.data ?? error?.response?.data;
  if (!d || typeof d !== 'object') return null;
  const list = Array.isArray(d.errors)
    ? d.errors
    : d.details && typeof d.details === 'object' && Array.isArray(d.details.errors)
      ? d.details.errors
      : null;
  if (!list || !list.length) return null;
  /** @type {Record<string, string>} */
  const errs = {};
  list.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const file = entry.file;
    if (typeof file !== 'string' || !file) return;
    errs[file] = stringifyPerFileError(entry.error);
  });
  return Object.keys(errs).length ? errs : null;
}

/**
 * Flatten legacy DRF errors or API v2 `{ code, message, details }` into one display string.
 * @param {Record<string, unknown>} [data] - Parsed JSON error body
 * @returns {string}
 */
export function normalizeApiErrorBody(data) {
  if (!data || typeof data !== 'object') return '';
  if (typeof data.code === 'string' && typeof data.message === 'string') {
    const fromDetails = collectMessagesFromDetails(data.details);
    if (fromDetails.length) return fromDetails.join('. ');
    return data.message;
  }
  const msgs = [];
  Object.keys(data).forEach((field) => {
    const v = data[field];
    if (Array.isArray(v)) {
      msgs.push(...v.filter((x) => typeof x === 'string'));
    } else if (field !== 'requires_verification' && field !== 'email') {
      if (typeof v === 'string') msgs.push(v);
      else if (v != null && typeof v !== 'object') msgs.push(String(v));
    }
  });
  return msgs.join('. ');
}

/**
 * Returns a user-friendly error message for API errors.
 * For 429 rate limit, shows backend message or translated "rate_limited"; otherwise
 * uses response/error body or error.message.
 * @param {Error} error - Error from apiService (may have error.status, error.data)
 * @param {(key: string) => string} [t] - i18n t function (e.g. from useTranslation), optional
 * @returns {string}
 */
export function getDisplayErrorMessage(error, t) {
  if (!error) return '';
  const body = error.data || error.response?.data;
  if (body && body.error === 'maintenance_mode') {
    if (typeof t === 'function') {
      const translated = t('common:maintenance.auth_notice');
      if (translated && translated !== 'common:maintenance.auth_notice') return translated;
    }
    return 'TuneWaves is briefly unavailable. We will be back when the work is done.';
  }
  const is429 =
    error.status === 429 ||
    error.data?.error === 'rate_limited' ||
    error.data?.code === 'rate_limited';
  if (is429) {
    if (error.data?.message) return error.data.message;
    if (typeof t === 'function') return t('common:messages.rate_limited');
    return 'Too many requests. Please wait and try again.';
  }
  const v2OrMixed = normalizeApiErrorBody(error.data || error.response?.data);
  if (v2OrMixed) return v2OrMixed;
  const fromBody = error.response?.data?.error || error.data?.error;
  if (fromBody && typeof fromBody === 'string') return fromBody;
  const fromMessage =
    typeof error.data?.message === 'string' ? error.data.message : '';
  if (fromMessage) return fromMessage;
  const fromArray =
    error.response?.data?.avatar?.[0] ||
    error.response?.data?.email?.[0] ||
    error.data?.avatar?.[0] ||
    error.data?.email?.[0];
  if (fromArray) return fromArray;
  const fromClientLiteral = translateKnownApiClientMessage(error.message, t);
  if (fromClientLiteral) return fromClientLiteral;
  return error.message || '';
}

/**
 * Auth login/register: use {@link getDisplayErrorMessage}, but drop api.js generic
 * messages ({@link GENERIC_API_FAILED_MESSAGE}, {@link GENERIC_API_FAILED_AFTER_REFRESH_MESSAGE})
 * when there is no clearer body-derived text so UI can show translated fallbacks.
 * @param {Error} error
 * @param {(key: string, ...args: unknown[]) => string} [t] - i18n `t`
 * @returns {string}
 */
export function getAuthApiDisplayMessage(error, t) {
  const s = getDisplayErrorMessage(error, t);
  if (
    !s ||
    s === GENERIC_API_FAILED_MESSAGE ||
    s === GENERIC_API_FAILED_AFTER_REFRESH_MESSAGE
  ) {
    return '';
  }
  return s;
}
